import { EnvironmentInjector, inject, Injectable, OnDestroy, runInInjectionContext } from "@angular/core";
import { getLocationInformation, GetLocationInformationData, GetLocationInformationVariables, getMyAccountInformation, getMyInteractions, GetMyInteractionsVariables, getMyUserInformationCheck, getStatusInformation } from "@dataconnect/account";
import { Store } from "@ngrx/store";
import { AccountActions, accountFeature } from "../store/account.store";
import { COLLECTION_PATH_ACCOUNT_CONFIG, ConsumeInvitationRequest, FireStoreStripeAccountRecord, FireStoreStripeAccountRecordOnboardingLink, LocationPrivate, LocationPublic, SegmentPublic } from "../../../globalTypes/account.types";
import { GetMyAccountInformationData } from "@dataconnect/account";
import { debounceTime, defer, filter, from, lastValueFrom, of, Subject, switchMap, take, takeUntil, tap } from "rxjs";
import { AuthUserActions, authUserFeature } from "../store/authUser.store";
import { Logger } from "../helper/logger";
import { CategoriesPageActions } from "../store/category.store";
import { Functions, httpsCallable, HttpsCallableResult } from '@angular/fire/functions';
import { COLLECTION_PATH_USERS, UserInvite } from "../../../globalTypes/auth.types";
// import { DataConnect } from "firebase/data-connect";
import { OrdersActions } from "../store/order.store";
import { Cart, createBlankOrder, OrderPublic } from "../../../globalTypes/order.types";
import { getFormattedOrder, sleep } from "../../../globalTypes/_helpers";
import { snackBarError, uiErrorCatcher } from "../helper/error.handling";
import { MatSnackBar } from "@angular/material/snack-bar";
import { getCookie, setCookie } from "../helper/general";
import { Status } from "../../../globalTypes/base.types";
import { Firestore, doc, onSnapshot, updateDoc } from "@angular/fire/firestore";
import { blankBubbles, InteractionsPageActions } from "../store/interactions.store";
import { InteractionPublic } from "../../../globalTypes/interactions.types";
import { onCallFunctions } from "../../../globalTypes/backend.api.types";
import { LOCATION_LABEL } from "../../../globalTypes/configs";
const log = new Logger('AccountService');

@Injectable({
  providedIn: 'root'
})
export class AccountService implements OnDestroy {

  destroy$: Subject<void> = new Subject();
  locationDestroy$: Subject<void> = new Subject();
  locationId = '';
  interactionIteration = 0;
  interactionsLoadLimit = 20;
  interactionsLoadOffset = 0;
  envInjector = inject(EnvironmentInjector)
  fulfillmentBells = ["required","fulfilling","waiting","delinquency","terminated","delayed","incident","review"];
  purchaseBells = ["terminated","shipping","delayed","incident","arrived"];
  refreshAccount$: Subject<void> = new Subject();
  refreshTryLimit = 5;
  refreshTries = 0;
  redirectFlag = 'redirectedForSegmentMismatch';

  constructor(
    private store: Store,
    private functions: Functions,
    private snackBar: MatSnackBar,
    private db: Firestore,
    //private dc: DataConnect, // <-- keep this for some dumb reason
  ){
    this.store.select(accountFeature.selectLocation).pipe(takeUntil(this.destroy$)).subscribe(async location=>{

      if(location && location.id !== this.locationId){
        log.log({location})
        this.locationDestroy$.next();
        this.interactionsLoadOffset = 0;
        this.loadLocationInformation(location.id);
      }
    })
    this.refreshAccount$
      .pipe(
        takeUntil(this.destroy$),
        tap( ()=>{
          log.log('refreshAccount$ try triggered');
        }),
        debounceTime(1000),
        tap( ()=>{
          this.refreshTries++;
          if(this.refreshTries >= this.refreshTryLimit){
            this.snackBar.open("Error connecting to account user information, please reload the page","Dismiss")
            this.refreshAccount$.complete();
          }
          console.log('refreshAccount$ try #', this.refreshTries);
        }),
        switchMap( () => { 
          console.log('refreshAccount$ switchMap triggered end');
          return from(this._loadMyAccountInfo())} )
        ).subscribe()
  }

  async setupPaymentProcessor(locationId: string){
    const payload = { locationId };
    // TODO: If we get other payment processors, get the processor before calling createStripeAccount and replace it
    log.log({payload});
    const callable = httpsCallable(this.functions,  onCallFunctions.createStripeAccount);
    const result: HttpsCallableResult = await uiErrorCatcher(callable(payload), this.snackBar, "Error creating Stripe account");
    log.log({result});
    return result.data;
  }

  async loadMyAccountInfo(calledFrom: string = ''){
    log.log('loadMyAccountInfo called from : '+calledFrom)
    if(calledFrom === 'setUserAsAuthUser'){
      localStorage.removeItem(this.redirectFlag);
      this.refreshTries = 0;
    }
    // FIXME THIS RUNS THREE TIMES AFTER EMAIL VERIFY AND TWO ON LOGIN
    this.refreshAccount$.next();
  }
  async _loadMyAccountInfo(){
    // FIXME load locations with status "suspended" so listings can be filtered by default
    // FIXME or what happens if a listing that someone has bought gets deleted?
    const authUserRaw = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(take(1)))
    const authUser = authUserRaw ? JSON.parse(JSON.stringify(authUserRaw)) : undefined;
    if(authUser && authUser.isEmailVerified){
      try {
        const userRef = await getMyUserInformationCheck();
        const user = userRef.data.user;
        log.log({user, msg: "try to load User from db", refreshTries: this.refreshTries})
        if(!user){
          setTimeout(() => {
            this.refreshAccount$.next();
          }, 500);
          return;
        }
      } catch(error) {
        log.error('try to load User from db Error loading user for account information: ', this.refreshTries, error)
        setTimeout(() => {
          this.refreshAccount$.next();
        }, 500);
        return;
      }
      const myAccountInfoResults = await uiErrorCatcher(getMyAccountInformation(), this.snackBar, "Error loading account information");
      const myAccountInfo: GetMyAccountInformationData = myAccountInfoResults.data;
      log.log({myAccountInfo, noDefaultLocation: !authUser.defaultLocationId, hasLocations: myAccountInfo.locations.length > 0});
      
      // if user doesn't have a default location but does have locations, set the default location to the first location they have
      if(!authUser.defaultLocationId && myAccountInfo.userLocations.length){
        authUser.defaultLocationId = myAccountInfo.userLocations[0].locationId;
        await this.setDefaultLocationId(authUser.defaultLocationId, authUser.id);
        log.log('No default location set for user, setting to first available location: ', myAccountInfo.userLocations[0].locationId)
      }
      await sleep(1000);
      // if user is in initiated status, set them as initiated in the store
      if(myAccountInfo.user && myAccountInfo.user.statusId === "initiated"){
        this.store.dispatch(AccountActions.setUserStatusId({statusId: myAccountInfo.user.statusId}))
      }
      let segment: SegmentPublic | undefined;
      let location: LocationPublic | undefined;
      let locations: {[locationId: string]: LocationPublic} = {}
      if(myAccountInfo.locations && myAccountInfo.locations.length > 0){
        locations = myAccountInfo.locations.reduce((a: {[locationId: string]: LocationPublic}, l:any)=>{a[l.id]=l as LocationPublic; return a}, {} as {[locationId: string]: LocationPublic})
        this.store.dispatch(AccountActions.setLocations({locations}))
      }
      location = !authUser.defaultLocationId ? undefined : locations[authUser.defaultLocationId] as LocationPublic;
      // install locations in store and set selected location to default location if it exists, otherwise set to first location in list if it exists
      if(myAccountInfo.userLocations && myAccountInfo.userLocations.length > 0){
        const locationIds: string[] = myAccountInfo.userLocations.map(ul=>ul.locationId);
        const locs = locationIds.map(id=>locations[id]);
        log.log({locations: locs});
        if(locs && locations) {
          this.store.dispatch(AccountActions.setAvailableLocations({availableLocations:Object.values(locs) as unknown as LocationPublic[]}))
          if(location){
            this.store.dispatch(AccountActions.setLocation({location: location as unknown as LocationPrivate}))
          }
        }
      }
      // install segment info and check domain
      if(myAccountInfo.userSegments && myAccountInfo.userSegments.length > 0){
        segment = location && location.segmentId ? myAccountInfo.userSegments.find(us => us.segment.id === location.segmentId)?.segment as SegmentPublic : myAccountInfo.userSegments[0].segment as SegmentPublic;
        if(segment) {
          this.store.dispatch(AccountActions.setSegment({segment}))
          // check if subdomain matches the segment
          this.doubleCheckSubdomainMatchesSegmentAndRedirectIfNot(segment.subdomain);
        }
      }
      // save verified status of this user for this segment
      if(segment && (!authUser.isVerifiedForSegment || authUser.isVerifiedForSegment[segment?.id] === undefined) ){
        if(!authUser.isVerifiedForSegment) authUser.isVerifiedForSegment = {};
        const userSegment = myAccountInfo.userSegments.find(us=>us.segment.id === segment.id);
        const isVerified = userSegment ? userSegment.isVerified : false;
        this.store.dispatch(AuthUserActions.setIsVerifiedForSegment({segmentId: segment.id, isVerified: isVerified || false}))
      }
      if(myAccountInfo.categories && myAccountInfo.categories.length > 0){
        this.store.dispatch(CategoriesPageActions.categoriesLoaded({categories: myAccountInfo.categories}))
      }
    } else {
      log.log('No Email Verified Auth User to load account from')
    }
  }

  async setDefaultLocationId(locationId: string, uid: string): Promise<void>{
    await runInInjectionContext(this.envInjector, async ()=> {
      const path = COLLECTION_PATH_USERS + '/' + uid;
      log.log({path, locationId})
      const userDocRef = doc(this.db, path);
      await updateDoc(userDocRef, {defaultLocationId: locationId});
      this.store.dispatch(AuthUserActions.setDefaultLocation({locationId}))
    })
  }

  async loadLocationInformation(locationId: string){
    log.log('loadLocationInformation: ', locationId)
    this.locationId = locationId;
    const vars: GetLocationInformationVariables = { locationId }
    sleep(5000);
    let hasPaymentProcessorUser = false;
    try {
      const informationRef = await uiErrorCatcher(getLocationInformation(vars), this.snackBar, "Error loading location");
      const information: GetLocationInformationData = informationRef.data
      console.log({information})
      const authUser = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(filter(u=>!!u), take(1)));
      const cartOrder = (information.cart.length ? information.cart[0] : await createBlankOrder(authUser.id, locationId)) as OrderPublic;
      log.log({cartOrder});
      // order cart items by location and creation date
      try {
        const cart = getFormattedOrder(cartOrder, "location");
        this.store.dispatch(OrdersActions.updateCart({cart: cart as unknown as Partial<Cart>}))
      }catch(error: any){
        snackBarError(error.message || "Error formatting cart order", this.snackBar)
      }
      hasPaymentProcessorUser = !!information.location && information.location.stripeUsers_on_location && information.location.stripeUsers_on_location.length > 0;
    } catch(error){
      log.error({error})
    }
    if(hasPaymentProcessorUser) this.connectToInteractionCount()
  }

  async connectToInteractionCount(){
    // TODO this is triggered on signup before they have an account config
    // maybe load more in the information check to determine if it is time to call this or not.
    log.log('connectToInteractionCount')

    return runInInjectionContext(this.envInjector, ()=>{
      onSnapshot(doc(this.db, COLLECTION_PATH_ACCOUNT_CONFIG + "/" + this.locationId), {
        next: ((snapshot) => {
          if(!snapshot.exists()) {
            console.error(`No account config found for ${LOCATION_LABEL} ${this.locationId}, cannot connect to interaction updates`)
            this.snackBar.open("Error connecting to interaction updates for this "+LOCATION_LABEL+", some features may not work properly","Dismiss")
            return;
          }
          const data = snapshot.data() as FireStoreStripeAccountRecord
          if(data && data.interactionCount !== this.interactionIteration){
            this.hydrateInteractions(true)
          }
        })
      }) ;
    });
  }

  async hydrateInteractions (hydrateAll = false){
    const vars: GetMyInteractionsVariables = { locationId: this.locationId, limit: this.interactionsLoadLimit, offset: this.interactionsLoadOffset }
    if(hydrateAll){
      vars.offset = 0
      vars.limit = this.interactionsLoadLimit + this.interactionsLoadOffset;
      this.store.dispatch(InteractionsPageActions.resetBubbleAlerts())
    }
    console.log({vars})
    const interactionRef = await getMyInteractions(vars);
    if(!hydrateAll){
      this.interactionsLoadOffset += this.interactionsLoadLimit;
    }
    const interactions = interactionRef.data.interactions as unknown as InteractionPublic[];
    this.store.dispatch(InteractionsPageActions.interactionsLoaded({interactions}))
    // += bubble numbers // FIXME this will count old interactions that might not be relevant... needs to count on fulfillment / order // maybe fix by adding sets w/ fulfillmentIds instead of bubble numbers
    const bubbles = blankBubbles();
    for(let i=0; i < interactions.length; i++) {
      const interaction = interactions[i];
      const isBuyer = interaction.buyerId === this.locationId;
      const isAlert = isBuyer ? interaction.buyerAlert : interaction.sellerAlert;
      // console.log({interaction, statusId: interaction.fulfillment.statusId, isBuyer, isAlert})
      if(!isAlert) continue;
      if(!isBuyer && this.fulfillmentBells.includes(interaction.fulfillment.statusId)) bubbles.fulfillment.add(interaction.fulfillment.id)
      if(isBuyer && this.purchaseBells.includes(interaction.fulfillment.statusId)) bubbles.purchase.add(interaction.fulfillment.id)
      const seen = isBuyer ? interaction.buyerViewed : interaction.sellerViewed;
      if(!seen) bubbles.unseen.add(interaction.fulfillment.id)
    }
    if(hydrateAll){ this.store.dispatch(InteractionsPageActions.resetBubbleAlerts()) }
    this.store.dispatch(InteractionsPageActions.addBubbles(bubbles));
  }
  
  async acceptInvite(invite: UserInvite): Promise<boolean>{
    const authUser = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(take(1)))
    log.log({authUser, invite});
    if(!authUser || !invite || !invite.id || !invite.isActive) return false;
    //update user to have a default location
    //consume invite
    //add user location record
    const payload: ConsumeInvitationRequest = { invitationId: invite.id, skipMakeDefault: false }
    log.log({payload});
    const callable = httpsCallable<ConsumeInvitationRequest,boolean>(this.functions, onCallFunctions.consumeInvitation);
    const result: HttpsCallableResult<boolean> = await uiErrorCatcher(callable(payload), this.snackBar, "Error consuming invitation");
    log.log({result});
    if(result.data){
      this.snackBar.open("Invitation accepted, you have been added to this location","Dismiss");
    }
    return result.data;
  }
  
  async refreshOnboardingLink(locationId: string): Promise<FireStoreStripeAccountRecordOnboardingLink>{
    const payload = { locationId }
    const callable = httpsCallable<{locationId: string},FireStoreStripeAccountRecordOnboardingLink>(this.functions, 'refreshOnboardingLink');
    const result: HttpsCallableResult<FireStoreStripeAccountRecordOnboardingLink> = await uiErrorCatcher(callable(payload), this.snackBar, "Error refreshing onboarding link");
    log.log({result});
    return result.data;
  }

  async getStatuses(type: "fulfillments" | "refunds" | "orders" | undefined) {
    // GetStatusInformation
    let selector: any = accountFeature.selectStatuses;
    switch(type){
      case "fulfillments": selector = accountFeature.selectFulfillmentStatuses;
        break;
      case "refunds": selector = accountFeature.selectRefundStatuses;
        break;
      case "orders": selector = accountFeature.selectOrderStatuses;
        break;
    }
    let statuses: Status[] = await lastValueFrom(this.store.select<Status[]>(selector).pipe(take(1)))
    if(statuses && statuses.length) return statuses;
    const cookieStatusStr = getCookie('siteStatuses')
    try{
      if(cookieStatusStr){
        statuses = JSON.parse(cookieStatusStr)
      }
    }catch(error: any){
      console.log({cookieStatusStr})
    }
    if(!statuses || !statuses.length){
      const statusesRef = await uiErrorCatcher(getStatusInformation(), this.snackBar, "Error getting site status information")
      statuses = await statusesRef.data.statuses as unknown as Status[];
      console.log({statuses})
      setCookie('siteStatuses', JSON.stringify(statuses), 1);
    }
    if(statuses){
      this.store.dispatch(AccountActions.setStatuses({statuses}));
      return type ? await lastValueFrom(this.store.select<Status[]>(selector).pipe(take(1))) : statuses;
    } else {
      return [];
    }
  }

  doubleCheckSubdomainMatchesSegmentAndRedirectIfNot(subdomain: string){
    const currentSubdomain = window.location.hostname.split('.')[0];
    if(subdomain !== currentSubdomain){
      log.warn(`Current subdomain ${currentSubdomain} does not match segment subdomain ${subdomain}. This may cause unexpected behavior.`)
      // redirect to the same url, but with the correct subdomain and store a flag in local storage to prevent an infinite redirect loop in case of some weird edge case where the subdomain check is wrong
      if(!localStorage.getItem(this.redirectFlag)){
        localStorage.setItem(this.redirectFlag, 'true');
        const hostname = window.location.hostname
        const hostParts = hostname.split('.');
        const partsThatMatter = hostParts.filter(part=>!["dev","staging","com"].includes(part));
        if(partsThatMatter.length > 2){
          hostParts[0] = subdomain;
        }else if(partsThatMatter.length === 1){
          hostParts.unshift(subdomain);
        }
        const newHostname = hostParts.join('.');
        console.log({hostname, hostParts, partsThatMatter, newHostname})
        if(newHostname !== window.location.hostname){
          const newUrl = window.location.href.replace(window.location.hostname, newHostname);
          log.log(`Redirecting to ${newUrl} to match segment subdomain`);
          // TODO, put up a dialog for users to click and be redirected to the right subdomain?
          window.location.href = newUrl;
        }
      } else {
        log.warn('Already redirected for segment mismatch, not redirecting again to prevent loop');
      }
    }
  }

  ngOnDestroy(): void {
    this.locationDestroy$.next();
    this.locationDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}

